import { useState } from 'react';
import { graphql, useMutation } from 'react-relay';

import useHumanVerification from '@/hooks/use-human-verification';
import { relayMutationToPromise } from '@/utils/relay';

import type { useCompareCreateAreaPreviewMutation } from './__generated__/useCompareCreateAreaPreviewMutation.graphql';

export type CompareAddressCreateFailure = 'ADDRESS_NOT_FOUND';

/** Resolves the picked suggestion into an area preview. */
const useCompareCreateAreaPreview = () => {
  const humanVerification = useHumanVerification('landlord_area_preview');
  const [failure, setFailure] = useState<CompareAddressCreateFailure | null>(
    null,
  );
  const [commit, isCommitting] =
    useMutation<useCompareCreateAreaPreviewMutation>(graphql`
      mutation useCompareCreateAreaPreviewMutation(
        $input: LandlordCreateAreaPreviewInput!
        $turnstileToken: String!
      ) {
        landlordCreateAreaPreview(
          input: $input
          turnstileToken: $turnstileToken
        ) {
          preview {
            id
          }
          failures
        }
      }
    `);

  const createPreview = async (addressId: string): Promise<string | null> => {
    setFailure(null);

    try {
      const turnstileToken = await humanVerification.getToken();
      const { response } = await relayMutationToPromise(commit, {
        variables: { input: { addressId }, turnstileToken },
      });
      const result = response.landlordCreateAreaPreview;
      const createdPreviewId = result?.preview?.id;
      const nextFailure = result?.failures?.[0];

      if (nextFailure != null || createdPreviewId == null) {
        setFailure('ADDRESS_NOT_FOUND');
        return null;
      }

      return createdPreviewId;
    } catch (error) {
      console.error(error);
      setFailure('ADDRESS_NOT_FOUND');
      return null;
    } finally {
      humanVerification.reset();
    }
  };

  return {
    createPreview,
    failure,
    isCreating: isCommitting || humanVerification.isVerifying,
    verificationWidget: humanVerification.widget,
  };
};

export default useCompareCreateAreaPreview;
